import { Link } from 'react-router-dom';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { Card, CardContent, Badge } from '@/components/ui';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useFilters } from '@/contexts/FilterContext';
import { format, parseISO } from 'date-fns';
import type { Event } from '@/api/endpoints/bars';

interface EventCardProps {
  event: Event & { barName?: string; barId?: string; barLogoUrl?: string; distance?: number };
  showBar?: boolean;
  compact?: boolean;
}

// Format distance in miles
function formatDistance(meters: number | undefined): string | null {
  if (meters === undefined) return null;
  const miles = meters / 1609;
  return miles < 0.1 ? '< 0.1 mi' : `${miles.toFixed(1)} mi`;
}

export function EventCard({
  event,
  showBar = true,
  compact = true,
}: EventCardProps) {
  const { isFavorite } = useUserPreferences();
  const { locationSource } = useFilters();
  const barIsFavorite = event.barId ? isFavorite(event.barId) : false;
  const startDate = event.startDatetime ? parseISO(event.startDatetime) : null;
  const endDate = event.endDatetime ? parseISO(event.endDatetime) : null;
  // Only show distance when using GPS (not when viewing a city page)
  const distanceText =
    locationSource === 'gps' ? formatDistance(event.distance) : null;

  // Treat recurring events without time info as all-day
  const isEffectivelyAllDay = event.isAllDay || (!startDate && event.rrule);

  const cardContent = (
    <Card lift className={`overflow-hidden ${barIsFavorite ? 'card-favorite' : ''}`}>
      <CardContent className="relative p-3 overflow-hidden">
        {/* Background logo - faded, right-aligned */}
        {event.barLogoUrl && (
          <img
            src={event.barLogoUrl}
            alt=""
            aria-hidden="true"
            className="card-logo-bg"
          />
        )}
        {/* Content */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-secondary/10">
            <Calendar className="h-5 w-5 text-accent-secondary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className={`text-base font-semibold ${compact ? 'truncate' : ''}`}>
                {event.name}
              </h3>
              {isEffectivelyAllDay && <Badge>All Day</Badge>}
            </div>
            {event.description && (
              <p
                className={`mt-1 text-sm text-muted ${compact ? 'line-clamp-2' : ''}`}
              >
                {event.description}
              </p>
            )}
            {startDate && !event.isAllDay && (
              <div className="mt-2 flex items-center gap-2 text-sm text-muted">
                <Clock className="h-4 w-4" />
                <span>
                  {format(startDate, 'h:mm a')}
                  {endDate && ` - ${format(endDate, 'h:mm a')}`}
                </span>
              </div>
            )}
            {showBar && event.barName && (
              <p className="mt-2 flex items-center gap-1 text-sm text-muted">
                <MapPin className="h-3 w-3" />
                {event.barName}
                {distanceText && ` • ${distanceText}`}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Wrap in Link if we have a bar to navigate to
  if (showBar && event.barId) {
    return (
      <Link to={`/bars/${event.barId}`} className="block">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}
